import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Building2, Check, X, Upload, History } from "lucide-react";
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function StoreProductsManagement() {
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [assignedProductIds, setAssignedProductIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [viewFilter, setViewFilter] = useState("all"); // all | assigned | unassigned
  // 並び順スナップショット: 「すべて」表示時に選択中を先頭に並べる際の基準。
  // チェックの切替では更新せず（＝クリックで商品が左右に飛ばない）、店舗読込/一括操作時のみ更新する。
  const assignedRef = useRef([]);

  // 選択を一括で差し替える（並び順スナップショットも更新）
  const applyAssigned = (ids) => {
    const next = Array.isArray(ids) ? ids : [];
    assignedRef.current = next;
    setAssignedProductIds(next);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [storesRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/stores`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders()),
      ]);
      setStores(storesRes.data);
      // 同一idの重複商品を除外（本番の重複データ混入に対する保険）
      const seenIds = new Set();
      const uniqueProducts = (productsRes.data || []).filter((p) => {
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
      });
      setProducts(uniqueProducts);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSelect = async (storeId) => {
    setSelectedStoreId(storeId);
    if (!storeId) {
      applyAssigned([]);
      return;
    }

    try {
      // 店舗情報を再取得
      const storeRes = await axios.get(`${API}/stores`, getAuthHeaders());
      setStores(storeRes.data);
      
      const store = storeRes.data.find((s) => s.id === storeId);
      
      if (!store) {
        toast.error("店舗が見つかりませんでした");
        return;
      }
      
      // available_product_idsが設定されている場合はそれを使用
      // 未設定（undefined/null）の場合のみ全商品を選択
      // 空配列の場合は空のまま
      if (store.available_product_ids === undefined || store.available_product_ids === null) {
        // 初回未設定の場合、デフォルトで全商品を選択
        applyAssigned(products.map((p) => p.id));
      } else {
        // 設定済み（空配列含む）の場合はその値を使用
        applyAssigned(store.available_product_ids);
      }
    } catch (error) {
      console.error("店舗商品取得エラー:", error);
      toast.error("店舗商品の取得に失敗しました");
    }
  };

  const toggleProduct = (productId) => {
    // 並び順スナップショット(assignedRef)は更新しない＝チェック切替でカードが移動しない
    setAssignedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSave = async () => {
    if (!selectedStoreId) {
      toast.error("店舗を選択してください");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/stores/${selectedStoreId}/products`,
        assignedProductIds,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'application/json',
          }
        }
      );
      console.log("保存成功:", response.data);
      toast.success(`店舗商品を更新しました（${assignedProductIds.length}件）`);
      // 件数一覧に即時反映
      setStores((prev) =>
        prev.map((s) =>
          s.id === selectedStoreId
            ? { ...s, available_product_ids: [...assignedProductIds] }
            : s
        )
      );
      
      // 保存後は現在の選択状態を維持（再読み込み不要）
      // assignedProductIdsはすでに正しい状態なので、そのまま
    } catch (error) {
      console.error("保存エラー:", error);
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const selectAll = () => {
    applyAssigned(products.map((p) => p.id));
  };

  const deselectAll = () => {
    applyAssigned([]);
  };

  // 発注履歴から商品を取り込む
  const importFromOrderHistory = async () => {
    if (!selectedStoreId) {
      toast.error("店舗を選択してください");
      return;
    }

    setImporting(true);
    try {
      // 店舗の発注履歴を取得
      const response = await axios.get(
        `${API}/orders?store_id=${selectedStoreId}`,
        getAuthHeaders()
      );
      
      const orders = response.data;
      
      if (orders.length === 0) {
        toast.info("この店舗の発注履歴がありません");
        return;
      }

      // 発注履歴から商品IDを抽出（重複削除）
      const historicalProductIds = [...new Set(
        orders
          .filter(order => order.product_id)
          .map(order => order.product_id)
      )];

      if (historicalProductIds.length === 0) {
        toast.info("発注履歴に商品が見つかりませんでした");
        return;
      }

      // 既存の商品リストに追加（重複を避ける）
      const newProductIds = [...new Set([...assignedProductIds, ...historicalProductIds])];
      const addedCount = newProductIds.length - assignedProductIds.length;

      applyAssigned(newProductIds);
      toast.success(`発注履歴から${addedCount}件の商品を追加しました`);
    } catch (error) {
      console.error("発注履歴取得エラー:", error);
      toast.error("発注履歴の取得に失敗しました");
    } finally {
      setImporting(false);
    }
  };

  // CSVまたはExcelから商品コードをインポート
  const handleCSVImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedStoreId) {
      toast.error("先に店舗を選択してください");
      event.target.value = "";
      return;
    }

    try {
      let productCodes = new Set();

      // Excelファイルの場合
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const arrayBuffer = await file.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);
        
        // xlsx ライブラリを使用
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        // ヘッダー行を検出(優先順位: 商品番号 > 商品コード)
        let headerRowIndex = -1;
        let codeColumnIndex = -1;

        for (let i = 0; i < Math.min(5, jsonData.length); i++) {
          const row = jsonData[i];
          if (Array.isArray(row)) {
            // 優先順位1: 商品番号
            let foundIndex = row.findIndex(cell => {
              const cellStr = String(cell || '').toLowerCase();
              return cellStr.includes('商品番号') || cellStr.includes('product_number');
            });
            
            // 優先順位2: 商品コード(商品番号が見つからない場合のみ)
            if (foundIndex === -1) {
              foundIndex = row.findIndex(cell => {
                const cellStr = String(cell || '').toLowerCase();
                return cellStr.includes('商品コード') || cellStr.includes('product_code') || cellStr.includes('code');
              });
            }
            
            if (foundIndex !== -1) {
              headerRowIndex = i;
              codeColumnIndex = foundIndex;
              break;
            }
          }
        }

        // ヘッダーが見つからない場合、最後の列（商品番号列）を使用
        if (codeColumnIndex === -1) {
          // サンプルの構造: 最後の列が商品番号
          const sampleRow = jsonData.find(row => Array.isArray(row) && row.length > 0);
          if (sampleRow) {
            codeColumnIndex = sampleRow.length - 1;
            headerRowIndex = 0;
          }
        }

        // データ行を処理
        for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (Array.isArray(row) && row[codeColumnIndex] != null) {
            const code = String(row[codeColumnIndex]).trim();
            if (code && code !== '0' && !code.toLowerCase().includes('商品')) {
              productCodes.add(code);
            }
          }
        }
      } 
      // CSV/TXTファイルの場合
      else {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result);
          reader.onerror = () => reject(new Error("ファイル読み込みエラー"));
          reader.readAsText(file, "UTF-8");
        });

        // CSVをパース（カンマまたはタブ区切り）
        const lines = text.split("\n").filter(line => line.trim());
        let headerRowIndex = -1;
        let codeColumnIndex = -1;

        // ヘッダー行を検出
        for (let i = 0; i < Math.min(3, lines.length); i++) {
          const columns = lines[i].split(/[,\t]/).map(col => col.trim().replace(/["\r]/g, ""));
          const foundIndex = columns.findIndex(col => {
            const colStr = col.toLowerCase();
            return colStr.includes('商品番号') || 
                   colStr.includes('商品コード') || 
                   colStr.includes('product_code') ||
                   colStr.includes('code');
          });
          if (foundIndex !== -1) {
            headerRowIndex = i;
            codeColumnIndex = foundIndex;
            break;
          }
        }

        // ヘッダーが見つからない場合、最後の列を使用
        if (codeColumnIndex === -1) {
          const sampleLine = lines[0];
          const columns = sampleLine.split(/[,\t]/);
          codeColumnIndex = columns.length - 1;
          headerRowIndex = 0;
        }

        // データ行を処理
        for (let i = headerRowIndex + 1; i < lines.length; i++) {
          const columns = lines[i].split(/[,\t]/).map(col => col.trim().replace(/["\r]/g, ""));
          if (columns[codeColumnIndex]) {
            const code = columns[codeColumnIndex].trim();
            if (code && code !== '0' && !code.toLowerCase().includes('商品')) {
              productCodes.add(code);
            }
          }
        }
      }

      if (productCodes.size === 0) {
        toast.error("商品コードが見つかりませんでした");
        return;
      }

      // 商品コードから商品IDを取得
      const matchedProductIds = products
        .filter(p => productCodes.has(p.code))
        .map(p => p.id);

      if (matchedProductIds.length === 0) {
        toast.error("一致する商品が見つかりませんでした");
        return;
      }

      // 既存の商品リストに追加
      const newProductIds = [...new Set([...assignedProductIds, ...matchedProductIds])];
      const addedCount = newProductIds.length - assignedProductIds.length;

      // FAX履歴として記録し、取扱商品にも追加（発注画面の「FAX履歴」タブに表示される）
      try {
        await axios.post(
          `${API}/admin/store-products/fax-import`,
          { store_id: selectedStoreId, product_ids: matchedProductIds },
          getAuthHeaders()
        );
      } catch (e) {
        console.error("FAX履歴記録エラー:", e);
      }

      applyAssigned(newProductIds);
      toast.success(
        `${addedCount}件の商品を追加しました（FAX履歴に登録／${productCodes.size}件中${matchedProductIds.length}件が一致）`
      );
    } catch (error) {
      console.error("ファイル読み込みエラー:", error);
      toast.error("ファイルの読み込みに失敗しました");
    } finally {
      event.target.value = "";
    }
  };

  // 検索とカテゴリフィルタリング
  const filteredProducts = products.filter((product) => {
    // 検索フィルタ
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        product.name.toLowerCase().includes(term) ||
        product.code.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    
    // カテゴリフィルタ
    if (categoryFilter && product.category_id !== categoryFilter) {
      return false;
    }

    // 選択状態フィルタ（選択中のみ / 未選択のみ）
    if (viewFilter === "assigned" && !assignedProductIds.includes(product.id)) {
      return false;
    }
    if (viewFilter === "unassigned" && assignedProductIds.includes(product.id)) {
      return false;
    }

    return true;
  });

  // 「すべて」表示時は選択中の商品を先頭にまとめる。
  // 並び基準は assignedRef（スナップショット）を使用し、チェック切替では並びが変わらない
  // ＝クリックした商品が左右に飛ばず、意図しない商品にチェックが入る誤操作を防ぐ。
  const displayedProducts =
    viewFilter === "all"
      ? [...filteredProducts].sort((a, b) => {
          const aOn = assignedRef.current.includes(a.id) ? 0 : 1;
          const bOn = assignedRef.current.includes(b.id) ? 0 : 1;
          return aOn - bOn;
        })
      : filteredProducts;

  if (loading) {
    return <div className="text-center py-8">読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-slate-600" />
          店舗別商品設定
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          店舗ごとに発注可能な商品を設定します
        </p>
      </div>

      {/* 取扱商品件数 一覧（CSV取込結果の確認用） */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="store-product-counts">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-800">
            取扱商品件数 一覧
          </h2>
          <span className="text-xs text-slate-500">{stores.length} 店舗</span>
        </div>
        <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">店舗コード</th>
                <th className="px-4 py-2 text-left">店舗名</th>
                <th className="px-4 py-2 text-right">取扱商品件数</th>
                <th className="px-4 py-2 text-center">状態</th>
              </tr>
            </thead>
            <tbody>
              {[...stores]
                .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                .map((store) => {
                  const ids = store.available_product_ids;
                  const notSet = ids === undefined || ids === null;
                  const count = notSet ? products.length : ids.length;
                  return (
                    <tr
                      key={store.id}
                      onClick={() => handleStoreSelect(store.id)}
                      data-testid={`store-count-row-${store.code}`}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-emerald-50 ${
                        selectedStoreId === store.id ? "bg-emerald-50" : ""
                      }`}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{store.code}</td>
                      <td className="px-4 py-2 text-slate-800">{store.name}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">
                        {count}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {notSet ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">
                            未設定（全商品）
                          </span>
                        ) : count === 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                            0件
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                            設定済み
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          ※ 行をクリックすると、その店舗の商品設定を下で編集できます。CSV取込後に件数が反映されているかご確認ください。
        </p>
      </div>

      {/* 店舗選択 */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <Label className="text-sm font-medium text-slate-700">店舗を選択</Label>
        <select
          value={selectedStoreId}
          onChange={(e) => handleStoreSelect(e.target.value)}
          className="mt-2 w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          <option value="">店舗を選択してください</option>
          {stores.map((store) => (
            <option key={store.id} value={store.id}>
              {store.name} ({store.code})
            </option>
          ))}
        </select>
      </div>

      {/* 商品一覧 */}
      {selectedStoreId && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              商品一覧（{assignedProductIds.length} / {products.length}件選択中）
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={selectAll}
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
              >
                全て選択
              </Button>
              <Button
                onClick={deselectAll}
                variant="outline"
                size="sm"
                className="text-slate-600"
              >
                全て解除
              </Button>
            </div>
          </div>

          {/* 発注履歴インポート機能 */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <History className="h-4 w-4" />
              発注履歴から商品を取り込む
            </h3>
            <p className="text-xs text-blue-700 mb-3">
              過去の発注履歴やCSVファイルから商品を一括で追加できます
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={importFromOrderHistory}
                disabled={importing}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <History className="h-4 w-4 mr-2" />
                {importing ? "取り込み中..." : "システム内の発注履歴から取り込み"}
              </Button>
              {/* デスク見積取込は保留中: <DeskQuoteImportDialog storeId={selectedStoreId} assignedProductIds={assignedProductIds} onApply={applyAssigned} /> */}
              <label className="inline-block">
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={handleCSVImport}
                  className="hidden"
                  id="csv-upload"
                />
                <Button
                  as="span"
                  size="sm"
                  variant="outline"
                  className="cursor-pointer border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={() => document.getElementById("csv-upload")?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  CSV/Excelファイルから取り込み
                </Button>
              </label>
            </div>
            <div className="mt-2 text-xs text-blue-600">
              ※ CSV/Excelファイルの「商品番号」または「商品コード」列から商品を自動検出します。取り込んだ商品は発注画面の「FAX履歴」タブに表示されます。
            </div>
          </div>

          {/* 検索とカテゴリフィルタ */}
          <div className="mb-4 space-y-3">
            {/* 表示切替: すべて / 選択中のみ / 未選択のみ */}
            <div className="flex flex-wrap items-center gap-2" data-testid="assigned-view-filter">
              <span className="text-xs font-medium text-slate-500 mr-1">表示:</span>
              <Button
                onClick={() => setViewFilter("all")}
                variant={viewFilter === "all" ? "default" : "outline"}
                size="sm"
                data-testid="view-filter-all"
                className={viewFilter === "all" ? "bg-slate-700 hover:bg-slate-800 text-white" : "text-slate-600"}
              >
                すべて（{products.length}）
              </Button>
              <Button
                onClick={() => setViewFilter("assigned")}
                variant={viewFilter === "assigned" ? "default" : "outline"}
                size="sm"
                data-testid="view-filter-assigned"
                className={viewFilter === "assigned" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "text-emerald-600 border-emerald-600 hover:bg-emerald-50"}
              >
                <Check className="h-4 w-4 mr-1" />
                選択中のみ（{assignedProductIds.length}）
              </Button>
              <Button
                onClick={() => setViewFilter("unassigned")}
                variant={viewFilter === "unassigned" ? "default" : "outline"}
                size="sm"
                data-testid="view-filter-unassigned"
                className={viewFilter === "unassigned" ? "bg-slate-700 hover:bg-slate-800 text-white" : "text-slate-600"}
              >
                未選択のみ（{Math.max(0, products.length - assignedProductIds.length)}）
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="">全カテゴリ</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="商品名または商品コードで検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
            </div>
            {(searchTerm || categoryFilter || viewFilter !== "all") && (
              <p className="text-sm text-slate-600">
                {filteredProducts.length}件の商品が見つかりました
                {viewFilter === "assigned" && " (選択中のみ)"}
                {viewFilter === "unassigned" && " (未選択のみ)"}
                {categoryFilter && ` (カテゴリ: ${categories.find(c => c.id === categoryFilter)?.name})`}
              </p>
            )}
          </div>

          {/* 商品グリッド */}
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded p-4">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                検索条件に一致する商品がありません
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {displayedProducts.map((product) => {
                const isAssigned = assignedProductIds.includes(product.id);
                return (
                  <div
                    key={product.id}
                    onClick={() => toggleProduct(product.id)}
                    className={`
                      flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer transition-all
                      ${
                        isAssigned
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }
                    `}
                  >
                    <div
                      className={`
                        flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center
                        ${
                          isAssigned
                            ? "border-emerald-500 bg-emerald-500"
                            : "border-slate-300 bg-white"
                        }
                      `}
                    >
                      {isAssigned && <Check className="h-4 w-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {product.name}
                        {product.unit && (
                          <span className="ml-1.5 px-1.5 py-0 rounded text-[11px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                            {product.unit}
                          </span>
                        )}
                        {product.sub_display && (
                          <span
                            className="ml-1.5 px-1.5 py-0 rounded text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-300"
                            data-testid={`product-subdisplay-${product.code}`}
                            title="デスクの補助表示"
                          >
                            {product.sub_display}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {product.code}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>

          {/* 保存ボタン */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? "保存中..." : "保存"}
            </Button>
          </div>
        </div>
      )}

      {!selectedStoreId && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center text-slate-500">
          店舗を選択してください
        </div>
      )}
    </div>
  );
}
